import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { RoleSelector } from './RoleSelector';
import { Loader2 } from 'lucide-react';
import { addUserRole, deactivateUserRole } from '@/lib/firebase/queries/roles';
import { getActiveRoles } from '@/lib/utils/roles';
import type { User, UserRole } from '@/types';

interface UserRoleManagerProps {
  user: User;
  onUpdate?: () => void;
}

export function UserRoleManager({ user, onUpdate }: UserRoleManagerProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const { currentUser, hasRole } = useAuth();
  const { t, dir } = useLanguage();

  // Get active roles
  const activeRoles = getActiveRoles(user.roles);

  const handleRoleToggle = async (role: UserRole) => {
    if (!currentUser?.uid) {
      toast.error(t('dashboard.admin.users.errors.notLoggedIn'));
      return;
    }

    if (!hasRole('admin')) {
      toast.error(t('dashboard.admin.users.errors.noPermission'));
      return;
    }

    try {
      setIsUpdating(true);

      if (!activeRoles.includes(role)) {
        await addUserRole(user.id, role, currentUser.uid);
        toast.success(t('dashboard.admin.users.roleManager.roleAdded', { role: t(`dashboard.admin.users.roles.${role.toLowerCase()}`) }));
      } else {
        await deactivateUserRole(user.id, role);
        toast.success(t('dashboard.admin.users.roleManager.roleRemoved', { role: t(`dashboard.admin.users.roles.${role.toLowerCase()}`) }));
      }

      onUpdate?.();
    } catch (error: any) {
      console.error('Error updating roles:', error);
      toast.error(error.message || t('dashboard.admin.users.errors.updateFailed'));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-4" dir={dir}>
      <RoleSelector
        selectedRoles={activeRoles}
        onRoleToggle={handleRoleToggle}
        disabled={isUpdating || !hasRole('admin')}
      />

      {isUpdating && (
        <div className="flex items-center justify-center text-sm text-muted-foreground py-4">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          {t('dashboard.admin.users.roleManager.updating')}
        </div>
      )}
    </div>
  );
}